import {
  Alert,
  Button,
  Form,
  Modal,
  Select,
  Spacing,
  Typography,
} from "@p0security/ui";
import { TenantAwareLink } from "components/common/TenantAwareLink";
import { useCallback, useMemo, useState } from "react";

import { useOauthServers } from "./AgenticGateway";
import { oauthRegistrationUrl } from "./oauthRegistration";

export type OauthRegistrationModalProps = {
  canManageServers: boolean;
  clientId: string;
  onClose: () => void;
  open: boolean;
};

/** Sends the operator to the gateway to bind an MCP server's upstream OAuth
 * credential to a federated client.
 *
 * The gateway drives the whole flow in the browser (sign-in, consent, and the
 * upstream authorization), so this only chooses the server and links there.
 */
export const OauthRegistrationModal: React.FC<OauthRegistrationModalProps> = ({
  canManageServers,
  clientId,
  onClose,
  open,
}) => {
  const { error, loading, servers } = useOauthServers();
  const [serverId, setServerId] = useState<string>();

  const options = useMemo(
    () => servers.map(({ id }) => ({ label: id, value: id })),
    [servers]
  );

  const url = useMemo(() => {
    const server = servers.find(({ id }) => id === serverId);
    return server
      ? oauthRegistrationUrl(server.endpoint, {
          backendId: server.id,
          clientId,
        })
      : undefined;
  }, [clientId, serverId, servers]);

  // Reset only once the close animation ends: the link closes the modal as it
  // is followed, and clearing the selection sooner would drop its href
  // mid-click.
  const resetSelection = useCallback(() => setServerId(undefined), []);

  const noServers = !error && !loading && servers.length === 0;

  return (
    <Modal
      afterClose={resetSelection}
      centered
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        // A link rather than `window.open`, so the browser treats the new tab
        // as user-initiated and the operator can also copy or middle-click it.
        <Button
          disabled={!url}
          href={url}
          key="continue"
          rel="noopener noreferrer"
          target="_blank"
          type="primary"
          onClick={onClose}
        >
          Continue to gateway
        </Button>,
      ]}
      onCancel={onClose}
      open={open}
      title="Register OAuth credential"
      width={560}
    >
      <Typography.Paragraph type="secondary">
        The gateway opens in a new tab. Sign in, then authorize the MCP server
        for this client. The client can then use the server without a user
        present.
      </Typography.Paragraph>
      {error && (
        <Spacing asChild mb={16}>
          <Alert message={error} showIcon type="error" />
        </Spacing>
      )}
      <Form layout="vertical">
        <Form.Item
          extra={
            noServers ? (
              <>
                No installed MCP servers use OAuth credentials.
                {canManageServers && (
                  <>
                    {" "}
                    <TenantAwareLink to="integrations/agentic/server">
                      Manage MCP servers
                    </TenantAwareLink>
                  </>
                )}
              </>
            ) : (
              "Only servers that obtain OAuth credentials from their upstream are listed."
            )
          }
          label="MCP server"
        >
          <Select
            aria-label="MCP server"
            loading={loading}
            notFoundContent={
              loading
                ? "Loading MCP servers…"
                : (error ?? "No OAuth MCP servers installed")
            }
            options={options}
            placeholder="Select an MCP server"
            status={error ? "error" : undefined}
            value={serverId}
            virtual={false}
            onChange={setServerId}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
